import React from "react";
import {css, Theme, useTheme} from "@emotion/react";

import {CenterBox} from "@pg-design/grid-module";
import {backgroundColor, flex, flexAbsoluteCenter, mt, mv, onDesktop, w100} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {Chart, ChartOptions} from "@pg-mono/charts";
import {rpAppLink} from "@pg-mono/rp-routes";
import {priceFormat, priceM2Format} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {IRegionReportPrice} from "../../../../region/types/IRegionReportPrice";
import {maps} from "../../utils/district_maps";
import {pricesReportAnalytics, PricesReportGTMViewAction} from "../../utils/prices_report_analytics";
import {IDistrictData} from "./PricesMapDistricts";

interface IProps {
    districtsData: IDistrictData[];
    region: IRegionReportPrice;
}

export const DistrictsChart = (props: IProps) => {
    const theme = useTheme();
    const {isMobile} = useUserDevice();

    const mapData = maps[props.region.slug as string][0];

    const getLinkToDistrict = (slug: string) => {
        return rpAppLink.offer.listFriendly({
            friendlySlug: `nowe-mieszkania-${props.region.slug}-${slug}`
        });
    };
    const getValuesRanges = (type: "min" | "max") => {
        if (type === "min") {
            let min = 0;

            props.districtsData.map((district) => {
                if (!min || min === 0 || district.avg_price_m2 < min) {
                    min = district.avg_price_m2;
                }
            });

            return Math.floor(min);
        }
        if (type === "max") {
            let max = 0;

            props.districtsData.map((district) => {
                if (district.avg_price_m2 > max) {
                    max = district.avg_price_m2;
                }
            });

            return Math.floor(max);
        }
    };

    const minValue = getValuesRanges("min") as number;
    const maxValue = getValuesRanges("max") as number;

    const options: ChartOptions = {
        chart: {
            type: "map",
            spacing: isMobile ? [0, 10, 0, 10] : [10, 10, 100, 10],
            height: isMobile ? null : "50%",
            animation: false,
            backgroundColor: "transparent",
            style: {
                fontFamily: "'Poppins', sans-serif"
            }
        },
        colorAxis: {
            maxColor: "#DF628F",
            min: minValue < 5000 ? minValue : 5000,
            max: maxValue,
            type: "logarithmic",
            endOnTick: false
        },
        legend: {
            enabled: false
        },
        colors: ["#DF628F", "#E084A5", "#E1ABBE", "#E2C2CE"],
        series: [
            {
                ...mapData,
                data: props.districtsData.map((el) => ({
                    id: el.slug_district,
                    name: el.district,
                    value: el.avg_price_m2
                })),
                point: {
                    events: {
                        click: function () {
                            pricesReportAnalytics.gtm.viewEvent(PricesReportGTMViewAction.INVEST_MAP_CLICK);
                            // @ts-ignore
                            window.open(getLinkToDistrict(this.id), "_blank");
                        }
                    }
                },
                nullInteraction: true,
                cursor: "pointer",
                states: {
                    inactive: {
                        opacity: 1
                    }
                }
            }
        ],
        plotOptions: {
            map: {
                borderWidth: 0,
                nullColor: `${theme.colors.gray[200]}`,
                states: {
                    hover: {
                        brightness: 0,
                        color: isMobile ? undefined : "#C93168"
                    }
                }
            }
        },
        title: {
            text: ""
        },
        tooltip: {
            enabled: !isMobile,
            borderRadius: 8,
            borderColor: "transparent",
            backgroundColor: "#FFF",
            formatter: function () {
                return `<b style="font-size: 1.9rem;">${this.point.value ? `${priceM2Format(this.point.value as number)}` : "Brak danych"}</b></br><span>${
                    this.point.name
                }</span>`;
            },
            shared: true
        }
    };

    return (
        <>
            <Chart
                height="100%"
                type="mapChart"
                options={options}
                loader={
                    props.districtsData ? null : (
                        <CenterBox height="300px">
                            <Loader size="lg" />
                        </CenterBox>
                    )
                }
            />

            <div css={scaleWrapper}>
                <div css={w100}>
                    <div css={flex("normal", "space-between")}>
                        <Text variant="info_txt_2" color={theme.colors.gray[800]}>
                            {priceM2Format(minValue)}
                        </Text>
                        <Text variant="info_txt_2" color={theme.colors.gray[800]}>
                            {priceM2Format(maxValue)}
                        </Text>
                    </div>

                    <div css={legendBarStyle} />

                    <Text variant="info_txt_1" align="center" color={theme.colors.gray[800]}>
                        Średnia cena za metr
                    </Text>
                </div>
            </div>

            <div css={[mt(3), w100]}>
                <table
                    role="presentation"
                    cellSpacing="0"
                    cellPadding="20"
                    style={{
                        border: 0,
                        width: isMobile ? "100%" : "60%",
                        margin: "2rem auto 4rem"
                    }}
                >
                    <thead>
                        <tr css={tableHeadWrapper}>
                            <th css={tal}>Dzielnica</th>
                            <th>Cena za m2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.districtsData.map((district) => {
                            return (
                                <React.Fragment key={district.slug_district}>
                                    {district.avg_price_m2 && (
                                        <tr key={district.district} css={listElement}>
                                            <td>
                                                <a href={getLinkToDistrict(district.slug_district)} css={flex("center", "space-between")}>
                                                    {district.district}
                                                </a>
                                            </td>
                                            <td css={tac}>
                                                {priceFormat(district.avg_price_m2)}/m<sup>2</sup>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const tal = css`
    text-align: left;
`;
const tac = css`
    text-align: center;
`;

const tableHeadWrapper = css`
    font-weight: bold;
`;

const listElement = (theme: Theme) => css`
    &:nth-of-type(even) {
        ${backgroundColor(theme.colors.gray[200])};
    }
`;

const scaleWrapper = css`
    ${flexAbsoluteCenter};
    min-width: 260px;

    ${onDesktop(css`
        display: flex;
        align-self: end;
        margin-right: 20%;
    `)};
`;

const legendBarStyle = css`
    height: 1.1rem;
    ${mv(1)};
    background: linear-gradient(to right, #e2c2ce, #df628f);
`;
import React from "react";
import {css, Theme, useTheme} from "@emotion/react";

import {CenterBox} from "@pg-design/grid-module";
import {backgroundColor, flex, flexAbsoluteCenter, mt, mv, onDesktop, w100} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {Chart, ChartOptions} from "@pg-mono/charts";
import {rpAppLink} from "@pg-mono/rp-routes";
import {priceFormat, priceM2Format} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {IRegionReportPrice} from "../../../../region/types/IRegionReportPrice";
import {maps} from "../../utils/district_maps";
import {pricesReportAnalytics, PricesReportGTMViewAction} from "../../utils/prices_report_analytics";
import {IDistrictData} from "./PricesMapDistricts";

interface IProps {
    districtsData: IDistrictData[];
    region: IRegionReportPrice;
}

export const DistrictsChart = (props: IProps) => {
    const theme = useTheme();
    const {isMobile} = useUserDevice();

    const mapData = maps[props.region.slug as string][0];

    const getLinkToDistrict = (slug: string) => {
        return rpAppLink.offer.listFriendly({
            friendlySlug: `nowe-mieszkania-${props.region.slug}-${slug}`
        });
    };
    const getValuesRanges = (type: "min" | "max") => {
        if (type === "min") {
            let min = 0;

            props.districtsData.map((district) => {
                if (!min || min === 0 || district.avg_price_m2 < min) {
                    min = district.avg_price_m2;
                }
            });

            return Math.floor(min);
        }
        if (type === "max") {
            let max = 0;

            props.districtsData.map((district) => {
                if (district.avg_price_m2 > max) {
                    max = district.avg_price_m2;
                }
            });

            return Math.floor(max);
        }
    };

    const minValue = getValuesRanges("min") as number;
    const maxValue = getValuesRanges("max") as number;

    const options: ChartOptions = {
        chart: {
            type: "map",
            spacing: isMobile ? [0, 10, 0, 10] : [10, 10, 100, 10],
            height: isMobile ? null : "50%",
            animation: false,
            backgroundColor: "transparent",
            style: {
                fontFamily: "'Poppins', sans-serif"
            }
        },
        colorAxis: {
            maxColor: "#DF628F",
            min: minValue < 5000 ? minValue : 5000,
            max: maxValue,
            type: "logarithmic",
            endOnTick: false
        },
        legend: {
            enabled: false
        },
        colors: ["#DF628F", "#E084A5", "#E1ABBE", "#E2C2CE"],
        series: [
            {
                ...mapData,
                data: props.districtsData.map((el) => ({
                    id: el.slug_district,
                    name: el.district,
                    value: el.avg_price_m2
                })),
                point: {
                    events: {
                        click: function () {
                            pricesReportAnalytics.gtm.viewEvent(PricesReportGTMViewAction.INVEST_MAP_CLICK);
                            // @ts-ignore
                            window.open(getLinkToDistrict(this.id), "_blank");
                        }
                    }
                },
                nullInteraction: true,
                cursor: "pointer",
                states: {
                    inactive: {
                        opacity: 1
                    }
                }
            }
        ],
        plotOptions: {
            map: {
                borderWidth: 0,
                nullColor: `${theme.colors.gray[200]}`,
                states: {
                    hover: {
                        brightness: 0,
                        color: isMobile ? undefined : "#C93168"
                    }
                }
            }
        },
        title: {
            text: ""
        },
        tooltip: {
            enabled: !isMobile,
            borderRadius: 8,
            borderColor: "transparent",
            backgroundColor: "#FFF",
            formatter: function () {
                return `<b style="font-size: 1.9rem;">${this.point.value ? `${priceM2Format(this.point.value as number)}` : "Brak danych"}</b></br><span>${
                    this.point.name
                }</span>`;
            },
            shared: true
        }
    };

    return (
        <>
            <Chart
                height="100%"
                type="mapChart"
                options={options}
                loader={
                    props.districtsData ? null : (
                        <CenterBox height="300px">
                            <Loader size="lg" />
                        </CenterBox>
                    )
                }
            />

            <div css={scaleWrapper}>
                <div css={w100}>
                    <div css={flex("normal", "space-between")}>
                        <Text variant="info_txt_2" color={theme.colors.gray[800]}>
                            {priceM2Format(minValue)}
                        </Text>
                        <Text variant="info_txt_2" color={theme.colors.gray[800]}>
                            {priceM2Format(maxValue)}
                        </Text>
                    </div>

                    <div css={legendBarStyle} />

                    <Text variant="info_txt_1" align="center" color={theme.colors.gray[800]}>
                        Średnia cena za metr
                    </Text>
                </div>
            </div>

            <div css={[mt(3), w100]}>
                <table
                    role="presentation"
                    cellSpacing="0"
                    cellPadding="20"
                    style={{
                        border: 0,
                        width: isMobile ? "100%" : "60%",
                        margin: "2rem auto 4rem"
                    }}
                >
                    <thead>
                        <tr css={tableHeadWrapper}>
                            <th css={tal}>Dzielnica</th>
                            <th>Cena za m2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.districtsData.map((district) => {
                            return (
                                <React.Fragment key={district.slug_district}>
                                    {district.avg_price_m2 && (
                                        <tr key={district.district} css={listElement}>
                                            <td>
                                                <a href={getLinkToDistrict(district.slug_district)} css={flex("center", "space-between")}>
                                                    {district.district}
                                                </a>
                                            </td>
                                            <td css={tac}>
                                                {priceFormat(district.avg_price_m2)}/m<sup>2</sup>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const tal = css`
    text-align: left;
`;
const tac = css`
    text-align: center;
`;

const tableHeadWrapper = css`
    font-weight: bold;
`;

const listElement = (theme: Theme) => css`
    &:nth-of-type(even) {
        ${backgroundColor(theme.colors.gray[200])};
    }
`;

const scaleWrapper = css`
    ${flexAbsoluteCenter};
    min-width: 260px;

    ${onDesktop(css`
        display: flex;
        align-self: end;
        margin-right: 20%;
    `)};
`;

const legendBarStyle = css`
    height: 1.1rem;
    ${mv(1)};
    background: linear-gradient(to right, #e2c2ce, #df628f);
`;
